import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ExternalLink, Plus, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

type Assignment = {
  id: string;
  asin: string;
  productTitle: string | null;
  status: string;
  rejectionReason: string | null;
  amazonUrl: string;
  submissionData?: {
    reviews?: StoredReview[];
    notes?: string;
    noReviewsReason?: string;
  } | null;
};

type ReviewInput = {
  amazonReviewId?: string;
  rating: number;
  title: string;
  body: string;
  reviewerName: string;
  reviewUrl?: string | null;
  verifiedPurchase: boolean;
  reviewDate: string;
  helpfulVotes?: number;
};

type StoredReview = {
  amazonReviewId?: string;
  rating?: number;
  title?: string | null;
  body?: string;
  reviewerName?: string | null;
  reviewUrl?: string | null;
  verifiedPurchase?: boolean;
  reviewDate?: string | null;
  helpfulVotes?: number;
};

function blankReview(): ReviewInput {
  return {
    rating: 1,
    title: "",
    body: "",
    reviewerName: "",
    reviewUrl: "",
    verifiedPurchase: false,
    reviewDate: "",
  };
}

function cleanTitle(title?: string | null): string {
  return (
    title
      ?.replace(/^\d+(?:\.\d+)?\s+out of 5 stars\s*/i, "")
      .trim() ?? ""
  );
}

function normalizeDateForInput(value?: string | null): string {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function reviewFromStored(review: StoredReview): ReviewInput {
  return {
    amazonReviewId: review.amazonReviewId,
    rating: review.rating ?? 1,
    title: cleanTitle(review.title),
    body: review.body ?? "",
    reviewerName: review.reviewerName ?? "",
    reviewUrl: review.reviewUrl ?? null,
    verifiedPurchase: review.verifiedPurchase ?? false,
    reviewDate: normalizeDateForInput(review.reviewDate),
    helpfulVotes: review.helpfulVotes,
  };
}

export default function ManualScraperScrapeSubmit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewInput[]>([blankReview()]);
  const [notes, setNotes] = useState("");
  const [noReviewsReason, setNoReviewsReason] = useState("no_negative_reviews");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAssignment = useCallback(async (): Promise<boolean> => {
    const res = await api<{ available: Assignment[]; claimed: Assignment[] }>(
      "/manual-scraper/scrapes"
    );
    if (!res.ok || !res.data) {
      navigate("/manual-scrape/login", { replace: true });
      return false;
    }
    const found =
      res.data.claimed.find((a) => a.id === id) ??
      res.data.available.find((a) => a.id === id) ??
      null;
    setAssignment(found);
    const capturedReviews = found?.submissionData?.reviews ?? [];
    if (capturedReviews.length > 0) {
      setReviews(capturedReviews.map(reviewFromStored));
    }
    if (found?.submissionData?.notes) {
      setNotes(found.submissionData.notes);
    }
    if (found?.submissionData?.noReviewsReason) {
      setNoReviewsReason(found.submissionData.noReviewsReason);
    }
    return true;
  }, [id, navigate]);

  useEffect(() => {
    let cancelled = false;
    loadAssignment().then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadAssignment]);

  useEffect(() => {
    function refreshOnFocus() {
      if (document.visibilityState === "visible") void loadAssignment();
    }
    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [loadAssignment]);

  function setReview(i: number, patch: Partial<ReviewInput>) {
    setReviews((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function removeReview(i: number) {
    setReviews((rs) => rs.filter((_, idx) => idx !== i));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Strip empty rows + coerce date strings.
    const payload = reviews
      .filter((r) => r.body.trim().length > 0)
      .map((r) => ({
        rating: r.rating,
        title: cleanTitle(r.title) || null,
        body: r.body.trim(),
        reviewerName: r.reviewerName.trim() || null,
        amazonReviewId: r.amazonReviewId,
        reviewUrl: r.reviewUrl?.trim() || null,
        verifiedPurchase: r.verifiedPurchase,
        reviewDate: r.reviewDate || null,
        helpfulVotes: r.helpfulVotes,
      }));
    const resolvedNotes =
      payload.length === 0
        ? [
            `No reviews submitted: ${noReviewsReason.replaceAll("_", " ")}`,
            notes.trim(),
          ]
            .filter(Boolean)
            .join("\n")
        : notes.trim();

    setSubmitting(true);
    const res = await api<{ ok: true; count: number }>(
      `/manual-scraper/scrapes/${id}/submit`,
      {
        method: "POST",
        body: JSON.stringify({
          reviews: payload,
          notes: resolvedNotes || undefined,
          noReviewsReason: payload.length === 0 ? noReviewsReason : undefined,
        }),
      }
    );
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Submission failed.");
      return;
    }
    navigate("/manual-scrape", { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 dark:bg-slate-950">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Assignment not found
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            It may have been reassigned. Pick another job.
          </p>
          <Link
            to="/manual-scrape"
            className="mt-4 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm text-white dark:bg-slate-100 dark:text-slate-900"
          >
            ← Back to portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-3xl px-6 py-8">
        <Link
          to="/manual-scrape"
          className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          ← Portal
        </Link>

        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h1 className="text-xl font-semibold tracking-tight">
            {assignment.productTitle || assignment.asin}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span className="font-mono">{assignment.asin}</span>
            <a
              href={assignment.amazonUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-200"
            >
              Open Amazon reviews <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs text-slate-600 dark:text-slate-400">
            <li>Open the Amazon reviews link above (sign in to your own Amazon account if asked)</li>
            <li>For each review you see, fill out a row below</li>
            <li>Click <strong>Submit</strong> when you've entered every review on the page</li>
          </ol>
          {assignment.rejectionReason && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              <strong>QA rejected last submission:</strong> {assignment.rejectionReason}
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-5">
          {reviews.map((r, i) => (
            <div
              key={i}
              className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Review {i + 1}</h3>
                {reviews.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeReview(i)}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </button>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Rating
                  </label>
                  <select
                    value={r.rating}
                    onChange={(e) => setReview(i, { rating: Number(e.target.value) })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n} ★
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Reviewer name
                  </label>
                  <input
                    type="text"
                    value={r.reviewerName}
                    onChange={(e) => setReview(i, { reviewerName: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Title
                  </label>
                  <input
                    type="text"
                    value={r.title}
                    onChange={(e) => setReview(i, { title: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="col-span-2">
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                      Review URL
                    </label>
                    {r.reviewUrl && (
                      <a
                        href={r.reviewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <input
                    type="url"
                    value={r.reviewUrl ?? ""}
                    onChange={(e) => setReview(i, { reviewUrl: e.target.value })}
                    placeholder="https://www.amazon.com/gp/customer-reviews/..."
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Review body <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={r.body}
                    onChange={(e) => setReview(i, { body: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Date (e.g. 2026-04-12)
                  </label>
                  <input
                    type="date"
                    value={r.reviewDate}
                    onChange={(e) => setReview(i, { reviewDate: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={r.verifiedPurchase}
                      onChange={(e) =>
                        setReview(i, { verifiedPurchase: e.target.checked })
                      }
                    />
                    Verified purchase
                  </label>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => setReviews((rs) => [...rs, blankReview()])}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            Add another review
          </button>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Notes for QA (optional)
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything unusual? Page layout changes, captchas, missing reviews, etc."
              className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </div>

          {reviews.filter((r) => r.body.trim()).length === 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide">
                Submit with 0 reviews
              </label>
              <select
                value={noReviewsReason}
                onChange={(e) => setNoReviewsReason(e.target.value)}
                className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-amber-800 dark:bg-slate-900 dark:text-slate-100"
              >
                <option value="no_negative_reviews">No negative reviews found</option>
                <option value="no_reviews">No reviews found</option>
                <option value="no_matching_star_filter">
                  No reviews matching requested star filter
                </option>
                <option value="amazon_page_unavailable">
                  Amazon page unavailable
                </option>
                <option value="duplicate_reviews_saved_elsewhere">
                  Reviews already saved to another product / duplicate
                </option>
                <option value="product_not_associated_with_brand">
                  Product not associated with brand
                </option>
              </select>
              <p className="mt-2 text-xs">
                This will submit the job for QA with 0 reviews and the selected reason.
              </p>
            </div>
          )}

          {error && (
            <p
              className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
              role="alert"
            >
              <AlertTriangle className="h-4 w-4" />
              {error}
            </p>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit {reviews.filter((r) => r.body.trim()).length} reviews
            </button>
            <Link
              to="/manual-scrape"
              className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            >
              Save & exit
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
